import { DEFAULT_LANGUAGE } from './languages';

export const CURRENCY_DISPLAY_COPY_VERSION_KEY = '_CURRENCY_DISPLAY_COPY_VERSION';
export const CURRENCY_DISPLAY_COPY_VERSION = 1;

/**
 * Translation entries whose meaning changed when rewards moved from points to
 * display money. Older locale bundles still contain point-specific wording,
 * so those entries must fall back to the current English copy until that
 * locale is regenerated from the versioned source file.
 */
const CURRENCY_DISPLAY_COPY_KEYS = [
  'LANDING_PAGE.HERO.SUBTITLE_PART_2',
  'LANDING_PAGE.HERO.ACTION_SUBTITLE',
  'LANDING_PAGE.HERO.CHECKLIST.ITEM_2',
  'LANDING_PAGE.HERO.LIST.ITEM_1.PART_3',
  'LANDING_PAGE.HOW_IT_WORKS.STEP_2.DESCRIPTION',
  'LANDING_PAGE.HOW_IT_WORKS.STEP_3.DESCRIPTION',
  'LANDING_PAGE.FAQ.QUESTIONS.MINIMUM_WITHDRAWAL.ANSWER',
  'LANDING_PAGE.FAQ.QUESTIONS.HOW_SURVEYS_WORK.ANSWER',
  'LANDING_PAGE.REVIEWS.STATS.ACTIVE_MEMBERS',
  'LANDING_PAGE.REWARDS.BENEFIT_3.TITLE',
  'LANDING_PAGE.REWARDS.PER_DAY',
  'LANDING_PAGE.FOOTER_DESCRIPTION',
  'LANDING_PAGE.GAMIFICATION.FEATURE_4.DESCRIPTION',
  'LANDING_PAGE.TRUST_SECTION.FEATURE_3.DESCRIPTION',
  'LANDING_PAGE.SEO.DESCRIPTION',
  'LANDING_PAGE.SEO.STRUCTURED_DESCRIPTION',
  'LANDING_PAGE.STEPS.TAKE_SURVEYS.TITLE',
  'LANDING_PAGE.STEPS.GET_PAID.DESCRIPTION_PART_1',
  'LANDING_PAGE.GAMES.SUBTITLE',
  'LANDING_PAGE.GAMES.POINTS',
  'TERMS_AND_CONDITIONS.APPLICABILITY.CONTENT',
  'TERMS_AND_CONDITIONS.REWARDS.CONTENT',
  'TERMS_AND_CONDITIONS.LIMITATION_OF_LIABILITY.CONTENT',
  'COMPONENTS.SURVEY_CARD.POINTS_SUFFIX',
  'COMPONENTS.SURVEY_CARD.ARIA_LABEL',
  'COMPONENTS.STREAK_OVERLAY.COINS_WITH_VALUE',
  'COMPONENTS.LEVEL_OVERLAY.COINS_WITH_VALUE',
  'COMPONENTS.POINTS_OVERLAY.CURRENT_BALANCE.HUNDRED_POINTS_VALUE',
  'COMPONENTS.POINTS_OVERLAY.LIFETIME_POINTS.TITLE',
  'COMPONENTS.REWARD.HUNDRED_POINTS_VALUE',
  'COMPONENTS.WELCOME_ABOARD_OVERLAY.BULLET_EARN_POINTS',
  'COMPONENTS.WELCOME_ABOARD_OVERLAY.EARN_POINTS_REWARDS',
  'COMPONENTS.TOPBAR.REWARD.HELPER_BADGE_TEXT',
  'COMPONENTS.REWARD_PROGRESS.POINTS',
  'COMPONENTS.REWARD_SELECTION.NO_REWARDS_FOR_BALANCE_DESCRIPTION',
  'COMPONENTS.MY_ACCOUNT_CARD.LIFETIME_POINTS',
  'COMPONENTS.MY_ACCOUNT_CARD.LIFETIME_PTS',
  'COMPONENTS.SECURITY_AND_ACCOUNT.DELETE_ACCOUNT_MODAL.TEXT_ERASES_ALL_DATA',
  'COMPONENTS.LIFETIME_POINTS_CARD.LIFETIME_POINTS',
  'COMPONENTS.ADJUST_REWARD_AMOUNT.FLEXIBLE_REWARDS_DESCRIPTION',
  'COMPONENTS.ADJUST_REWARD_AMOUNT.EARLY_REDEMPTION_DESCRIPTION',
  'FEATURES.AUTH.LOGIN_OR_SIGN_UP_MODAL.TEXT_SIGN_UP_BONUS_PART_3',
  'FEATURES.AUTH.TOS_MODAL.KEY_POINTS.ITEMS',
  'FEATURES.AUTH.TOS_MODAL.WHAT_WE_COVER.ITEMS',
  'FEATURES.AUTH.PRIVACY_POLICY_MODAL.KEY_FEATURES.ITEMS',
  'FEATURES.AUTH.PRIVACY_POLICY_MODAL.WHAT_THIS_POLICY_COVERS.ITEMS',
  'FEATURES.AFFILIATE.COMMISSION_FORMAT',
  'FEATURES.AFFILIATE.COMMISSION_RATE_TEXT',
  'FEATURES.ADJUST_REWARD_AMOUNT.FLEXIBLE_REWARDS_DESCRIPTION',
  'FEATURES.ADJUST_REWARD_AMOUNT.EARLY_REDEMPTION_DESCRIPTION',
  'PAGES.AFFILIATE_PAGE.STATS.TOTAL_EARNED.DESCRIPTION',
  'PAGES.AFFILIATE_PAGE.STATS.PENDING.DESCRIPTION',
  'PAGES.AFFILIATE_PAGE.REFERRAL_LINK.DESCRIPTION',
  'PAGES.AFFILIATE_PAGE.COMMISSIONS.POINTS_EARNED',
  'PAGES.HOME.DASHBOARD_REWARD_PROGRESS',
  'PAGES.HOME.SURVEY_FILTERS.MOST_POINTS',
  'PAGES.HOME.REWARD_SELECTION_BLOCKER_DESCRIPTION',
  'PAGES.HOME.POINTS_ABBREVIATION',
  'PAGES.MY_REWARDS_PAGE.POINTS_ABBREVIATION',
  'PAGES.TEMPORARY_DASHBOARD.TESTIMONIALS.2.TEXT',
  'FAQ_PAGE.SECTIONS.ABOUT_REWARDBLOOM.QUESTIONS.WHAT_DO_I_GET.ANSWER',
  'BONUS_POINTS.PAGE_TITLE',
  'BONUS_POINTS.PAGE_DESCRIPTION',
  'BONUS_POINTS.LOCKED_MESSAGE',
  'BONUS_POINTS.POINTS',
  'BONUS_POINTS.POINTS_ABBREVIATION',
  'UTILS.SEO.DASHBOARD_DESCRIPTION',
  'UTILS.SEO.DASHBOARD_KEYWORDS',
] as const;

type TranslationTree = Record<string, unknown>;

const removeTranslation = (translations: TranslationTree, key: string): void => {
  const path = key.split('.');
  let current: TranslationTree = translations;

  for (const segment of path.slice(0, -1)) {
    const next = current[segment];
    if (!next || typeof next !== 'object' || Array.isArray(next)) {
      return;
    }
    current = next as TranslationTree;
  }

  delete current[path[path.length - 1]];
};

export const parseVersionedTranslations = (data: string, languages?: string | string[]): TranslationTree => {
  const translations = JSON.parse(data) as TranslationTree;
  const language = Array.isArray(languages) ? languages[0] : languages;

  if (
    language === DEFAULT_LANGUAGE ||
    translations[CURRENCY_DISPLAY_COPY_VERSION_KEY] === CURRENCY_DISPLAY_COPY_VERSION
  ) {
    return translations;
  }

  for (const key of CURRENCY_DISPLAY_COPY_KEYS) {
    removeTranslation(translations, key);
  }

  return translations;
};
